// ITAM-2622: the single source of truth for "is this session a pharmacist
// consultant, and which persona is it". Pure — no store dependency — because the
// auth thunks need it at branch time, where all they have is a raw role array
// from the token/login response, while the selectors need it over Redux state.
//
// Naming warning: `selectIsPharmacist` (auth-slice) means the CALL-CENTER role
// (PHARMACIST_SUPPORT_USER / legacy "PHARMACIST"). The new consultant role must
// never be fed into it.

/**
 * Staff roles whose existing login branch must keep winning. Meeting any of
 * them suppresses pharmacist capability entirely (decision 6); the login refusal
 * for a medic (decision 6a) is the pre-existing branch chain doing what it
 * already does, not something this helper performs.
 *
 * PRODAJNO_MESTO_USER is deliberately NOT here — it is the confirmed dual-role
 * case (decision 5). APOTEKA_USER is deliberately not here either, and appears
 * nowhere in the condition below (decision 3).
 *
 * DELIVERY_USER is deliberately not here: the backend adds it to a CUSTOMER
 * account whenever their delivery order is completed (CnCOrderServiceImpl),
 * so the role is not evidence of a staff driver persona and must not suppress
 * a valid pharmacist session. A delivery-only token still resolves to DRIVER
 * through the branch chain, which only reaches that branch when no pharmacist
 * capability exists.
 */
export const CONFLICTING_STAFF = [
  "ADMIN",
  "MARKETPLACE_ADMIN_USER",
  "PHARMACIST_SUPPORT_USER",
  "MEDIC_SPECIALIST_USER", // decision 6a
];

export interface PharmacistPersona {
  /** The raw role is present. Never gate a consult surface on this alone. */
  hasPharmacistRole: boolean;
  hasRetailRole: boolean;
  /** The role is present AND no conflicting staff role outranks it. */
  hasSupportedPharmacistCapability: boolean;
  /** Consult-only pharmacist: capability without retail. */
  isConsultOnly: boolean;
  /** Dual-role pharmacist: capability plus retail (first-release scope). */
  isDualRetail: boolean;
}

export function derivePharmacistPersona(
  roles: string[] | null | undefined
): PharmacistPersona {
  const list = roles ?? [];
  const hasPharmacistRole = list.includes("PHARMACIST_USER");
  const hasRetailRole = list.includes("PRODAJNO_MESTO_USER");
  const hasSupportedPharmacistCapability =
    hasPharmacistRole && !list.some((r) => CONFLICTING_STAFF.includes(r));
  return {
    hasPharmacistRole,
    hasRetailRole,
    hasSupportedPharmacistCapability,
    isConsultOnly: hasSupportedPharmacistCapability && !hasRetailRole,
    isDualRetail: hasSupportedPharmacistCapability && hasRetailRole,
  };
}

/**
 * The persona identity used as a REMOUNT KEY (3.6). It must stay a scalar:
 * `${id}:${derivePharmacistPersona(roles)}` would stringify to
 * "42:[object Object]" — a key that never changes, silently defeating the
 * teardown it exists to perform.
 */
export type PersonaKey = "dual" | "consult" | "retail" | "other";

export function derivePersonaKey(
  roles: string[] | null | undefined
): PersonaKey {
  const persona = derivePharmacistPersona(roles);
  if (persona.isDualRetail) return "dual";
  if (persona.isConsultOnly) return "consult";
  if (persona.hasRetailRole) return "retail";
  return "other";
}
